import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { cn, formatMoney, formatNumber } from '@/lib/utils'
import { LoadingButton } from '@/components/loading-button'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { SearchSelect } from '@/components/search-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { SourceEmailLink } from '@/features/manufacturer-data/source-email-link'
import {
  type Group,
  type Measure,
  type LeadTimeChange,
  type QuotedVsActualPO,
  type QuotedVsActualRow,
  useLeadTimeHistory,
  useQuotedVsActual,
  useQuotedVsActualPurchaseOrders,
  useUpdateLeadTime,
} from './api'
import { days, notRecordedText, rowName, sourceText } from './format'
import { DaysGap, MoneyGap } from './shared'

type Level = 'product' | 'category' | 'manufacturer'

export function RowSheet({
  row,
  group,
  measure,
  months,
  onClose,
}: {
  row: QuotedVsActualRow | null
  group: Group
  measure: Measure
  months: number
  onClose: () => void
}) {
  return (
    <Sheet open={row != null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-2xl"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        {row && <SheetBody row={row} group={group} measure={measure} months={months} />}
      </SheetContent>
    </Sheet>
  )
}

function SheetBody({
  row,
  group,
  measure,
  months,
}: {
  row: QuotedVsActualRow
  group: Group
  measure: Measure
  months: number
}) {
  const lead = row.lead_time
  const context =
    group === 'product'
      ? [row.manufacturer?.name, row.category?.name].filter(Boolean).join(' · ')
      : group === 'category'
        ? (row.category?.path.slice(0, -1).map((step) => step.name).join(' › ') ?? '')
        : ''
  const pos = useQuotedVsActualPurchaseOrders({
    months,
    manufacturerUuid: group === 'category' ? undefined : row.manufacturer?.uuid,
    taxonomyNodeId: group === 'category' ? row.category?.id : undefined,
    productUuid: group === 'product' ? row.product?.uuid : undefined,
    enabled: true,
  })

  return (
    <>
      <SheetHeader className="border-b p-5">
        <SheetTitle className="text-lg">
          {group === 'product' && row.product ? (
            <Link
              to="/products/$productId"
              params={{ productId: row.product.uuid }}
              className="text-primary hover:underline"
            >
              {row.product.model}
            </Link>
          ) : group === 'manufacturer' && row.manufacturer ? (
            <Link
              to="/manufacturers/$manuId"
              params={{ manuId: row.manufacturer.uuid }}
              className="text-primary hover:underline"
            >
              {row.manufacturer.name}
            </Link>
          ) : (
            rowName(row)
          )}
        </SheetTitle>
        <SheetDescription className={cn(!context && 'sr-only')}>
          {context || `${rowName(row)} quoted vs actual`}
        </SheetDescription>
      </SheetHeader>

      <div className="space-y-6 p-5">
        {measure === 'lead_time' ? (
          <dl className="grid grid-cols-2 gap-4 text-sm tabular-nums sm:grid-cols-5">
            <Figure
              label="Quoted then"
              value={days(lead.quoted_then_days)}
              note={notRecordedText(lead.not_recorded_count, row.po_count)}
            />
            <Figure label="Set now" value={days(lead.quoted_days)} note={sourceText(lead)} />
            <Figure
              label="Actual"
              value={days(lead.actual_ship_days)}
              note={lead.shipped_count ? `${formatNumber(lead.shipped_count, 0)} shipped` : null}
            />
            <Figure label="Slowest" value={days(lead.slowest_ship_days)} />
            <Figure
              label="To delivery"
              value={days(lead.actual_delivery_days)}
              note={lead.delivered_count ? `${formatNumber(lead.delivered_count, 0)} delivered` : null}
            />
          </dl>
        ) : (
          <MoneyFigures row={row} measure={measure} />
        )}

        {measure === 'lead_time' && <LeadTimeForm key={row.key} row={row} group={group} months={months} />}

        {measure === 'lead_time' && <LeadTimeHistory row={row} group={group} />}

        <section aria-labelledby="qva-pos-heading" className="space-y-2">
          <h3 id="qva-pos-heading" className="text-sm font-semibold">
            Purchase orders
          </h3>
          {pos.isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-10 w-full" />
              ))}
            </div>
          ) : pos.error || !pos.data ? (
            <p className="text-sm text-muted-foreground">The POs couldn’t load. Close and reopen to try again.</p>
          ) : (
            <POTable pos={pos.data} measure={measure} showManufacturer={group === 'category'} />
          )}
        </section>
      </div>
    </>
  )
}

function Figure({ label, value, note }: { label: string; value: string; note?: string | null }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-base font-semibold">{value}</dd>
      {note && <dd className="text-xs text-muted-foreground">{note}</dd>}
    </div>
  )
}

function MoneyFigures({ row, measure }: { row: QuotedVsActualRow; measure: 'net' | 'freight' }) {
  const money = measure === 'net' ? row.net : row.freight
  if (money.po_count === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {measure === 'net' ? 'No fully invoiced POs yet.' : 'No POs with both a freight quote and a carrier cost yet.'}
      </p>
    )
  }
  return (
    <dl className="grid grid-cols-2 gap-4 text-sm tabular-nums sm:grid-cols-4">
      <Figure label="Quoted" value={formatMoney(money.quoted)} />
      <Figure
        label={measure === 'net' ? 'Invoiced' : 'Actual'}
        value={formatMoney(money.actual)}
        note={
          measure === 'freight' && money.awaiting_invoice_count
            ? `${formatNumber(money.awaiting_invoice_count, 0)} booked, not invoiced`
            : null
        }
      />
      <div>
        <dt className="text-xs text-muted-foreground">Difference</dt>
        <dd className="pt-1">
          <MoneyGap money={money} />
        </dd>
      </div>
      <Figure label="POs" value={formatNumber(money.po_count, 0)} />
    </dl>
  )
}

function levelOptions(row: QuotedVsActualRow, group: Group): Level[] {
  if (group === 'product') return row.category ? ['product', 'category', 'manufacturer'] : ['product', 'manufacturer']
  if (group === 'category') return ['category', 'manufacturer']
  return ['manufacturer']
}

function suggestedDays(lead: QuotedVsActualRow['lead_time']): string {
  const value = lead.actual_ship_days ?? lead.quoted_days
  return value == null ? '' : String(Math.round(value))
}

function LeadTimeForm({ row, group, months }: { row: QuotedVsActualRow; group: Group; months: number }) {
  const options = levelOptions(row, group)
  const [level, setLevel] = useState<Level>(options[0]!)
  const path = row.category?.path ?? []
  const [nodeId, setNodeId] = useState<number | undefined>(row.category?.id)
  // A category spans manufacturers; its lead time is set for one of them.
  const breakdown = useQuotedVsActual({
    group: 'manufacturer',
    measure: 'lead_time',
    months,
    taxonomyNodeId: row.category?.id,
    pageSize: 200,
    enabled: group === 'category' && row.category != null,
  })
  const manufacturers = group === 'category' ? (breakdown.data?.rows ?? []) : []
  const [pickedManufacturer, setPickedManufacturer] = useState<string | undefined>()
  const manufacturerUuid =
    group === 'category' ? (pickedManufacturer ?? manufacturers[0]?.manufacturer?.uuid) : row.manufacturer?.uuid
  const chosen = manufacturers.find((item) => item.manufacturer?.uuid === manufacturerUuid)
  const lead = group === 'category' ? (chosen?.lead_time ?? row.lead_time) : row.lead_time
  // Untouched, the field offers what the POs actually took.
  const [edited, setEdited] = useState<string | null>(null)
  const value = edited ?? suggestedDays(lead)
  const update = useUpdateLeadTime()

  const manufacturerName =
    group === 'category' ? chosen?.manufacturer?.name : row.manufacturer?.name
  const node = path.find((step) => step.id === nodeId)
  const levelLabels: Record<Level, string> = {
    product: 'This product',
    category: 'Category',
    manufacturer: manufacturerName ?? 'Manufacturer',
  }
  const current =
    level === 'product'
      ? lead.quoted_level === 'product'
      : level === 'category'
        ? lead.quoted_level === 'category' && lead.quoted_category?.id === nodeId
        : false
  const parsed = Number(value)
  const valid = value !== '' && Number.isInteger(parsed) && parsed >= 0 && parsed <= 365

  function save(days: number | null) {
    if (!manufacturerUuid) return
    update.mutate(
      {
        level,
        manufacturer_uuid: manufacturerUuid,
        product_uuid: level === 'product' ? row.product?.uuid : undefined,
        taxonomy_node_id: level === 'category' ? nodeId : undefined,
        lead_time_days: days,
      },
      {
        onSuccess: (result) => {
          const scope =
            level === 'product'
              ? row.product?.model
              : level === 'category'
                ? `${manufacturerName} ${node?.name ?? ''}`
                : manufacturerName
          toast.success(
            days == null
              ? `Lead time removed for ${scope}`
              : `${scope} now quotes ${days} ${days === 1 ? 'day' : 'days'} · ${formatNumber(result.products_changed, 0)} products`,
          )
        },
        onError: () => toast.error('The lead time wasn’t saved. Try again.'),
      },
    )
  }

  return (
    <section
      aria-labelledby="qva-set-heading"
      className="space-y-4 rounded-xl border bg-muted/30 p-4"
    >
      <h3 id="qva-set-heading" className="text-sm font-semibold">
        Set lead time
      </h3>

      {group === 'category' && (
        <div className="space-y-1.5">
          <Label htmlFor="qva-manufacturer">Manufacturer</Label>
          <SearchSelect
            id="qva-manufacturer"
            className="h-9 w-full bg-background"
            value={manufacturerUuid ?? undefined}
            options={manufacturers.flatMap((item) =>
              item.manufacturer
                ? [
                    {
                      value: item.manufacturer.uuid,
                      label: item.manufacturer.name,
                      description: `Quoted ${days(item.lead_time.quoted_days)}, actual ${days(item.lead_time.actual_ship_days)}`,
                    },
                  ]
                : [],
            )}
            onChange={(next) => {
              if (!next) return
              setPickedManufacturer(next)
              setEdited(null)
            }}
            placeholder={breakdown.isLoading ? 'Loading…' : 'Choose a manufacturer'}
            searchPlaceholder="Search manufacturers"
          />
        </div>
      )}

      {options.length > 1 && (
        <ReviewChoiceGroup
          label="Applies to"
          value={level}
          options={options.map((option) => ({ value: option, label: levelLabels[option] }))}
          onChange={setLevel}
        />
      )}

      {level === 'category' && path.length > 0 && (
        <div className="space-y-1.5">
          <Label htmlFor="qva-category">Category</Label>
          <Select value={nodeId ? String(nodeId) : ''} onValueChange={(next) => setNodeId(Number(next))}>
            <SelectTrigger id="qva-category" className="w-full bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[...path].reverse().map((step) => (
                <SelectItem key={step.id} value={String(step.id)}>
                  {step.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="qva-days">Business days</Label>
          <InlineFieldInput
            id="qva-days"
            type="number"
            inputMode="numeric"
            min={0}
            max={365}
            value={value}
            onChange={(event) => setEdited(event.target.value)}
            className="h-9 w-28 bg-background tabular-nums"
          />
        </div>
        <LoadingButton
          loading={update.isPending && update.variables?.lead_time_days != null}
          loadingText="Saving…"
          disabled={!valid || !manufacturerUuid || update.isPending}
          onClick={() => save(parsed)}
        >
          Set lead time
        </LoadingButton>
        {current && (
          <LoadingButton
            variant="outline"
            loading={update.isPending && update.variables?.lead_time_days == null}
            loadingText="Removing…"
            disabled={update.isPending}
            onClick={() => save(null)}
          >
            Remove
          </LoadingButton>
        )}
      </div>
    </section>
  )
}

function shortDate(value: string | null | undefined): string {
  if (!value) return '—'
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    // POs reach back up to two years; earlier years say which.
    year: year === new Date().getFullYear() ? undefined : 'numeric',
  })
}

function POTable({
  pos,
  measure,
  showManufacturer,
}: {
  pos: QuotedVsActualPO[]
  measure: Measure
  showManufacturer: boolean
}) {
  if (pos.length === 0) return <p className="text-sm text-muted-foreground">No POs in this period.</p>
  return (
    <>
      <ul className="divide-y rounded-lg border sm:hidden">
        {pos.map((po) => (
          <li key={po.uuid} className="flex items-start justify-between gap-3 p-3 text-sm">
            <div className="min-w-0">
              <POName po={po} showManufacturer={showManufacturer} />
              <p className="text-xs tabular-nums text-muted-foreground">
                {measure === 'lead_time'
                  ? `Quoted ${poQuotedThen(po)} · Sent ${shortDate(po.sent_on)} · Shipped ${shortDate(po.shipped_on)} · Delivered ${shortDate(po.delivered_on)}`
                  : `${shortDate(po.po_date)} · ${poMoneyLine(po, measure)}`}
              </p>
            </div>
            <div className="shrink-0">
              {measure === 'lead_time' ? <POActual po={po} /> : <POMoneyGap po={po} measure={measure} />}
            </div>
          </li>
        ))}
      </ul>
      <div className="hidden sm:block">
        <POWideTable pos={pos} measure={measure} showManufacturer={showManufacturer} />
      </div>
    </>
  )
}

function poQuotedThen(po: QuotedVsActualPO): string {
  return `${days(po.quoted_then_days)}${po.quoted_then_recorded ? '' : ' (not recorded)'}`
}

function poMoneyLine(po: QuotedVsActualPO, measure: 'net' | 'freight'): string {
  const quoted = measure === 'net' ? po.net_quoted : po.freight_quoted
  const actual = measure === 'net' ? po.net_actual : po.freight_actual
  const actualWord = measure === 'net' ? 'Invoiced' : po.freight_awaiting_invoice ? 'Booked' : 'Actual'
  return `Quoted ${quoted == null ? '—' : formatMoney(quoted)} · ${actualWord} ${
    actual == null ? (measure === 'net' ? 'not yet' : '—') : formatMoney(actual)
  }`
}

function POName({ po, showManufacturer }: { po: QuotedVsActualPO; showManufacturer: boolean }) {
  return (
    <>
      <Link
        to="/purchase-orders/$poId"
        params={{ poId: po.uuid }}
        className="font-medium text-primary hover:underline"
      >
        {po.po_number}
      </Link>
      {showManufacturer && (
        <p className="text-xs text-muted-foreground">
          <Link
            to="/manufacturers/$manuId"
            params={{ manuId: po.manufacturer.uuid }}
            className="hover:underline"
          >
            {po.manufacturer.name}
          </Link>
        </p>
      )}
    </>
  )
}

function POActual({ po }: { po: QuotedVsActualPO }) {
  if (po.ship_days == null) return <span className="text-muted-foreground">—</span>
  return (
    <DaysGap
      gap={po.quoted_days == null ? null : po.ship_days - po.quoted_days}
      quoted={po.quoted_days}
      label={days(po.ship_days)}
    />
  )
}

function POMoneyGap({ po, measure }: { po: QuotedVsActualPO; measure: 'net' | 'freight' }) {
  const quoted = measure === 'net' ? po.net_quoted : po.freight_quoted
  const actual = measure === 'net' ? po.net_actual : po.freight_actual
  if (quoted == null || actual == null) return <span className="text-muted-foreground">—</span>
  const difference = Number(actual) - Number(quoted)
  return (
    <MoneyGap
      money={{
        po_count: 1,
        quoted: String(quoted),
        actual: String(actual),
        difference: String(difference),
        difference_percent: Number(quoted) ? String((difference / Number(quoted)) * 100) : null,
        awaiting_invoice_count: 0,
      }}
    />
  )
}

function POWideTable({
  pos,
  measure,
  showManufacturer,
}: {
  pos: QuotedVsActualPO[]
  measure: Measure
  showManufacturer: boolean
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>PO</TableHead>
          {measure === 'lead_time' ? (
            <>
              <TableHead>Sent</TableHead>
              <TableHead>Shipped</TableHead>
              <TableHead className="text-right">Quoted then</TableHead>
              <TableHead className="text-right">Actual</TableHead>
              <TableHead>Delivered</TableHead>
            </>
          ) : (
            <>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Quoted</TableHead>
              <TableHead className="text-right">{measure === 'net' ? 'Invoiced' : 'Actual'}</TableHead>
              <TableHead className="text-right">Difference</TableHead>
            </>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {pos.map((po) => (
          <TableRow key={po.uuid}>
            <TableCell className="align-top">
              <POName po={po} showManufacturer={showManufacturer} />
            </TableCell>
            {measure === 'lead_time' ? (
              <>
                <TableCell className="align-top tabular-nums">{shortDate(po.sent_on)}</TableCell>
                <TableCell className="align-top tabular-nums">{shortDate(po.shipped_on)}</TableCell>
                <TableCell className="text-right align-top tabular-nums">
                  {days(po.quoted_then_days)}
                  {!po.quoted_then_recorded && <p className="text-xs text-muted-foreground">Not recorded</p>}
                </TableCell>
                <TableCell className="text-right align-top">
                  <POActual po={po} />
                </TableCell>
                <TableCell className="align-top tabular-nums">{shortDate(po.delivered_on)}</TableCell>
              </>
            ) : (
              <POMoneyCells po={po} measure={measure} />
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function POMoneyCells({ po, measure }: { po: QuotedVsActualPO; measure: 'net' | 'freight' }) {
  const quoted = measure === 'net' ? po.net_quoted : po.freight_quoted
  const actual = measure === 'net' ? po.net_actual : po.freight_actual
  const share = Number(po.share_percent)
  return (
    <>
      <TableCell className="align-top tabular-nums">{shortDate(po.po_date)}</TableCell>
      <TableCell className="text-right align-top tabular-nums">
        {quoted == null ? <span className="text-muted-foreground">—</span> : formatMoney(quoted)}
        {quoted != null && share < 99.95 && (
          <p className="text-xs text-muted-foreground">{formatNumber(share, 1)}% of PO</p>
        )}
      </TableCell>
      <TableCell className="text-right align-top tabular-nums">
        {actual == null ? (
          <span className="text-muted-foreground">
            {measure === 'net' ? 'Not invoiced' : '—'}
          </span>
        ) : (
          <>
            {formatMoney(actual)}
            {measure === 'freight' && po.freight_awaiting_invoice && (
              <p className="text-xs text-muted-foreground">Booked</p>
            )}
          </>
        )}
      </TableCell>
      <TableCell className="text-right align-top">
        <POMoneyGap po={po} measure={measure} />
      </TableCell>
    </>
  )
}

function LeadTimeHistory({ row, group }: { row: QuotedVsActualRow; group: Group }) {
  const history = useLeadTimeHistory({
    manufacturerUuid: group === 'category' ? undefined : row.manufacturer?.uuid,
    taxonomyNodeId: group === 'manufacturer' ? undefined : row.category?.id,
    productUuid: group === 'product' ? row.product?.uuid : undefined,
    enabled: true,
  })
  return (
    <section aria-labelledby="qva-history-heading" className="space-y-2">
      <h3 id="qva-history-heading" className="text-sm font-semibold">
        Lead time history
      </h3>
      {history.isLoading ? (
        <Skeleton className="h-10 w-full" />
      ) : history.error || !history.data ? (
        <p className="text-sm text-muted-foreground">The history couldn’t load. Close and reopen to try again.</p>
      ) : history.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">No changes yet.</p>
      ) : (
        <ul className="divide-y rounded-lg border">
          {history.data.map((change) => (
            <li key={change.id} className="flex items-start justify-between gap-3 p-3 text-sm">
              <div className="min-w-0">
                <p className="font-medium">{changeScope(change, group)}</p>
                <p className="text-xs text-muted-foreground">
                  {changeDate(change.changed_at)}
                  {changeWho(change) && ` · ${changeWho(change)}`}
                  {change.source_email && (
                    <>
                      {' · '}
                      <SourceEmailLink source={change.source_email} />
                    </>
                  )}
                </p>
              </div>
              <p className="shrink-0 tabular-nums">{changeValue(change)}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function changeScope(change: LeadTimeChange, group: Group): string {
  const manufacturer = change.manufacturer?.name ?? 'Manufacturer'
  if (change.level === 'product') return change.product?.model ?? 'Product'
  if (change.level === 'category') {
    const category = change.taxonomy_node?.name ?? 'Category'
    return group === 'category' ? `${manufacturer} · ${category}` : category
  }
  return manufacturer
}

function changeValue(change: LeadTimeChange): string {
  const next = change.new_days == null ? 'Removed' : days(change.new_days)
  return change.previous_days == null ? next : `${days(change.previous_days)} → ${next}`
}

function changeWho(change: LeadTimeChange): string | null {
  if (change.changed_by_user?.name) return change.changed_by_user.name
  if (change.changed_by === 'agent') return 'AI'
  return null
}

function changeDate(value: string): string {
  const date = new Date(value.endsWith('Z') ? value : `${value}Z`)
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  })
}
